import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { API_BASE_URL } from '../../lib/api';
import { useAdminAuth } from '../../context/AdminAuthContext';

interface EnterpriseUser {
  _id: string;
  email: string;
  name: string;
  subscription: {
    plan: string;
    limits: {
      textsPerMonth: number | 'unlimited';
      chatsPerMonth: number | 'unlimited';
      imagesPerMonth: number;
      metaAccounts: number | 'unlimited';
    };
    usage: {
      textsGenerated: number;
      chatsCreated: number;
      imagesGenerated: number;
    };
    isCustomPlan: boolean;
    customNote?: string;
  };
}

export function EnterpriseManagement() {
  const { isAuthenticated, logout } = useAdminAuth();
  const [users, setUsers] = useState<EnterpriseUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<EnterpriseUser | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Form state
  const [textsLimit, setTextsLimit] = useState<string>('unlimited');
  const [chatsLimit, setChatsLimit] = useState<string>('unlimited');
  const [imagesLimit, setImagesLimit] = useState<string>('1000');
  const [metaLimit, setMetaLimit] = useState<string>('unlimited');
  const [customNote, setCustomNote] = useState('');

  useEffect(() => {
    loadEnterpriseUsers();
  }, []);

  async function loadEnterpriseUsers() {
    try {
      const adminToken = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/enterprise-users`, {
        headers: {
          'Authorization': `Basic ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários Enterprise:', error);
    } finally {
      setLoading(false);
    }
  }

  function openEditModal(user: EnterpriseUser) {
    setEditingUser(user);
    setTextsLimit(user.subscription.limits.textsPerMonth === 'unlimited' ? 'unlimited' : String(user.subscription.limits.textsPerMonth));
    setChatsLimit(user.subscription.limits.chatsPerMonth === 'unlimited' ? 'unlimited' : String(user.subscription.limits.chatsPerMonth));
    setImagesLimit(String(user.subscription.limits.imagesPerMonth));
    setMetaLimit(user.subscription.limits.metaAccounts === 'unlimited' ? 'unlimited' : String(user.subscription.limits.metaAccounts));
    setCustomNote(user.subscription.customNote || '');
    setShowModal(true);
  }

  async function saveCustomLimits() {
    if (!editingUser) return;

    try {
      const adminToken = localStorage.getItem('adminToken');

      const limits = {
        textsPerMonth: textsLimit === 'unlimited' ? 'unlimited' : parseInt(textsLimit),
        chatsPerMonth: chatsLimit === 'unlimited' ? 'unlimited' : parseInt(chatsLimit),
        imagesPerMonth: parseInt(imagesLimit),
        metaAccounts: metaLimit === 'unlimited' ? 'unlimited' : parseInt(metaLimit)
      };

      const response = await fetch(`${API_BASE_URL}/admin/users/${editingUser._id}/custom-limits`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          limits,
          customNote: customNote.trim() || undefined
        })
      });

      if (response.ok) {
        alert('Limites customizados salvos com sucesso!');
        setShowModal(false);
        loadEnterpriseUsers();
      } else {
        const error = await response.json();
        alert(`Erro: ${error.message}`);
      }
    } catch (error) {
      console.error('Erro ao salvar limites:', error);
      alert('Erro ao salvar limites');
    }
  }

  async function resetUsage(userId: string) {
    if (!confirm('Deseja resetar o uso deste usuário? Os contadores serão zerados.')) {
      return;
    }

    try {
      const adminToken = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/reset-usage`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${adminToken}`
        }
      });

      if (response.ok) {
        alert('Uso resetado com sucesso!');
        loadEnterpriseUsers();
      } else {
        const error = await response.json();
        alert(`Erro: ${error.message}`);
      }
    } catch (error) {
      console.error('Erro ao resetar uso:', error);
      alert('Erro ao resetar uso');
    }
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[var(--cg-bg)]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-[var(--cg-surface)]/80 border-b" style={{ borderColor: 'var(--cg-border)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                  <i className="fa-solid fa-shield-halved text-white"></i>
                </div>
                <div>
                  <h1 className="font-bold text-lg">Admin Dashboard</h1>
                  <p className="text-xs opacity-60">CopyGenius</p>
                </div>
              </div>

              <Link
                to="/admin"
                className="px-4 py-2 rounded-lg hover:bg-[var(--cg-surface-alt)] text-sm font-medium transition-colors flex items-center gap-2"
              >
                <i className="fa-solid fa-dashboard"></i>
                Dashboard
              </Link>

              <Link
                to="/admin/enterprise"
                className="px-4 py-2 rounded-lg bg-brand-500/10 text-brand-400 text-sm font-medium transition-colors flex items-center gap-2"
              >
                <i className="fa-solid fa-crown"></i>
                Enterprise
              </Link>
            </div>

            <button
              onClick={logout}
              className="btn btn-danger h-9 px-4 text-xs"
            >
              <i className="fa-solid fa-right-from-bracket"></i> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {loading ? (
          <div className="text-center py-20">
            <i className="fa-solid fa-spinner fa-spin text-4xl opacity-50"></i>
            <p className="mt-4 opacity-60">Carregando usuários Enterprise...</p>
          </div>
        ) : (
          <>
            {/* Header do conteúdo */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">Gerenciamento Enterprise</h1>
              <p className="text-sm opacity-70">Configure limites customizados para clientes Enterprise</p>
            </div>

        {/* Users list */}
        {users.length === 0 ? (
          <div className="panel p-12 text-center">
            <i className="fa-solid fa-users text-4xl opacity-20 mb-4"></i>
            <p className="opacity-70">Nenhum usuário Enterprise encontrado</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {users.map(user => (
              <div key={user._id} className="panel p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold mb-1">{user.name}</h3>
                    <p className="text-sm opacity-70">{user.email}</p>
                    {user.subscription.customNote && (
                      <div className="mt-2 px-3 py-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20 inline-block">
                        <i className="fa-solid fa-star text-yellow-400 text-xs mr-2"></i>
                        <span className="text-xs">{user.subscription.customNote}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(user)}
                      className="px-4 py-2 rounded-lg bg-brand-500/10 hover:bg-brand-500/20 text-brand-400 text-sm font-medium transition-colors"
                    >
                      <i className="fa-solid fa-edit mr-2"></i>
                      Editar Limites
                    </button>
                    <button
                      onClick={() => resetUsage(user._id)}
                      className="px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-medium transition-colors"
                    >
                      <i className="fa-solid fa-rotate-right mr-2"></i>
                      Resetar Uso
                    </button>
                  </div>
                </div>

                {/* Limits grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <LimitCard
                    label="Textos/mês"
                    limit={user.subscription.limits.textsPerMonth}
                    usage={user.subscription.usage.textsGenerated}
                    icon="fa-file-lines"
                  />
                  <LimitCard
                    label="Chats/mês"
                    limit={user.subscription.limits.chatsPerMonth}
                    usage={user.subscription.usage.chatsCreated}
                    icon="fa-comments"
                  />
                  <LimitCard
                    label="Imagens/mês"
                    limit={user.subscription.limits.imagesPerMonth}
                    usage={user.subscription.usage.imagesGenerated}
                    icon="fa-images"
                  />
                  <LimitCard
                    label="Contas Meta"
                    limit={user.subscription.limits.metaAccounts}
                    usage={0}
                    icon="fa-star"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}

        {/* Edit Modal */}
        {showModal && editingUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-[var(--cg-surface)] rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Configurar Limites Enterprise</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 rounded-lg hover:bg-[var(--cg-surface-alt)] flex items-center justify-center"
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* User info */}
                <div className="pb-4 border-b" style={{borderColor: 'var(--cg-border)'}}>
                  <p className="text-sm opacity-70 mb-1">Usuário</p>
                  <p className="font-medium">{editingUser.name}</p>
                  <p className="text-sm opacity-60">{editingUser.email}</p>
                </div>

                {/* Limits */}
                <div className="grid gap-4">
                  <LimitInput
                    label="Textos por mês"
                    value={textsLimit}
                    onChange={setTextsLimit}
                    allowUnlimited
                  />
                  <LimitInput
                    label="Chats por mês"
                    value={chatsLimit}
                    onChange={setChatsLimit}
                    allowUnlimited
                  />
                  <LimitInput
                    label="Imagens por mês"
                    value={imagesLimit}
                    onChange={setImagesLimit}
                  />
                  <LimitInput
                    label="Contas Meta"
                    value={metaLimit}
                    onChange={setMetaLimit}
                    allowUnlimited
                  />
                </div>

                {/* Custom note */}
                <div>
                  <label className="block text-sm font-medium mb-2">Nota (visível para o usuário)</label>
                  <textarea
                    value={customNote}
                    onChange={(e) => setCustomNote(e.target.value)}
                    placeholder="Ex: Cliente VIP - Contrato especial - 10k imagens/mês"
                    className="w-full px-4 py-3 rounded-lg bg-[var(--cg-surface-alt)] border focus:outline-none focus:border-brand-500 transition-colors"
                    style={{borderColor: 'var(--cg-border)'}}
                    rows={3}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={saveCustomLimits}
                    className="flex-1 px-6 py-3 rounded-lg bg-brand-500 hover:bg-brand-600 text-white font-medium transition-colors"
                  >
                    Salvar Limites
                  </button>
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-6 py-3 rounded-lg bg-[var(--cg-surface-alt)] hover:bg-[var(--cg-surface)] transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function LimitCard({ label, limit, usage, icon }: { label: string; limit: number | 'unlimited'; usage: number; icon: string }) {
  const isUnlimited = limit === 'unlimited';
  const percentage = isUnlimited ? null : Math.round((usage / limit) * 100);

  return (
    <div className="bg-[var(--cg-surface-alt)] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <i className={`fa-solid ${icon} text-brand-400 text-sm`}></i>
        <span className="text-xs opacity-70">{label}</span>
      </div>
      <div className="text-2xl font-bold mb-1">
        {usage} {!isUnlimited && <span className="text-sm opacity-60">/ {limit}</span>}
      </div>
      {isUnlimited && <span className="text-xs text-brand-400">Ilimitado</span>}
      {!isUnlimited && percentage !== null && (
        <div className="mt-2 h-1 bg-slate-800/50 rounded-full overflow-hidden">
          <div className="h-full bg-brand-500" style={{width: `${Math.min(percentage, 100)}%`}}></div>
        </div>
      )}
    </div>
  );
}

function LimitInput({ label, value, onChange, allowUnlimited }: { label: string; value: string; onChange: (v: string) => void; allowUnlimited?: boolean }) {
  const [mode, setMode] = useState(value === 'unlimited' ? 'unlimited' : 'number');

  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <div className="flex gap-2">
        {allowUnlimited && (
          <div className="flex gap-2">
            <button
              onClick={() => { setMode('unlimited'); onChange('unlimited'); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'unlimited' ? 'bg-brand-500 text-white' : 'bg-[var(--cg-surface-alt)]'}`}
            >
              Ilimitado
            </button>
            <button
              onClick={() => { setMode('number'); onChange('100'); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'number' ? 'bg-brand-500 text-white' : 'bg-[var(--cg-surface-alt)]'}`}
            >
              Número
            </button>
          </div>
        )}
        {mode === 'number' && (
          <input
            type="number"
            value={value === 'unlimited' ? '100' : value}
            onChange={(e) => onChange(e.target.value)}
            min="0"
            className="flex-1 px-4 py-2 rounded-lg bg-[var(--cg-surface-alt)] border focus:outline-none focus:border-brand-500 transition-colors"
            style={{borderColor: 'var(--cg-border)'}}
          />
        )}
      </div>
    </div>
  );
}
